/**
 * KAN-735: чистые функции страницы "Расходы" и плашки на дашборде:
 * месяцы, строка-сводка, подписи списаний, курс, высота столбца графика.
 */
import { tokensSummaryPrefix } from '@/lib/expensesTokens';
import { formatRub, formatUsd } from '@/lib/money';
import type { ExpensesReport, WriteOffReason } from '@/types/expenses';

const MONTH_NAMES = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Месяц даты в виде YYYY-MM (по местному времени). */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
}

function splitMonth(month: string): { year: number; index: number } {
  const [y, m] = month.split('-').map(Number);
  return { year: y, index: m - 1 };
}

/** Сдвиг месяца YYYY-MM на delta месяцев. */
export function shiftMonth(month: string, delta: number): string {
  const { year, index } = splitMonth(month);
  const total = year * 12 + index + delta;
  return `${Math.floor(total / 12)}-${pad2((total % 12) + 1)}`;
}

/** "2026-10" -> "октябрь 2026". */
export function formatMonthLabel(month: string): string {
  const { year, index } = splitMonth(month);
  const name = MONTH_NAMES[index];
  return name ? `${name} ${year}` : month;
}

export function writeOffReasonLabel(reason: WriteOffReason): string {
  return reason === 'delete' ? 'Удалён' : 'Списан';
}

/** Ответ похож на отчёт о расходах (защита плашки от чужого или пустого ответа). */
export function isExpensesReport(data: unknown): data is ExpensesReport {
  if (!data || typeof data !== 'object') return false;
  const d = data as Partial<ExpensesReport>;
  return Array.isArray(d.items) && typeof d.total_rub === 'number';
}

/** Сумма статьи по коду, нет статьи: 0. */
export function itemAmount(report: ExpensesReport, code: string): number {
  return report.items.find((i) => i.code === code)?.amount_rub ?? 0;
}

/** Баланс OpenRouter в долларах или "нет данных". */
export function openRouterBalanceText(report: ExpensesReport): string {
  const usd = report.openrouter?.balance_usd;
  return usd === null || usd === undefined ? 'нет данных' : formatUsd(usd);
}

/**
 * Строка плашки дашборда:
 * "Расходы за <месяц>: X ₽ · прокси A · аккаунты B · токены C · баланс OpenRouter $D".
 * Токены - только если есть статья tokens (KAN-799: "токены по ключу" или
 * "токены (весь счёт)" по scope, сумма всего счёта в строку не идёт), баланс - только
 * если openrouter не null (траты OpenRouter видит только организация платформы).
 */
export function expensesSummaryLine(report: ExpensesReport): string {
  const parts = [
    `Расходы за ${formatMonthLabel(report.month)}: ${formatRub(report.total_rub)}`,
    `прокси ${formatRub(itemAmount(report, 'proxies'))}`,
    `аккаунты ${formatRub(itemAmount(report, 'accounts'))}`,
  ];
  const tokens = report.items.find((i) => i.code === 'tokens');
  if (tokens) parts.push(`${tokensSummaryPrefix(tokens)} ${formatRub(tokens.amount_rub)}`);
  if (report.openrouter) parts.push(`баланс OpenRouter ${openRouterBalanceText(report)}`);
  return parts.join(' · ');
}

/** Курс из поля ввода: число больше нуля, запятая как точка; иначе null. */
export function parseRate(raw: string): number | null {
  const text = (raw ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const RATE_ERROR = 'Курс: число больше нуля';

/** Высота столбца графика: живёт рядом с моделью графика токенов (KAN-799). */
export { barHeightPercent } from '@/lib/expensesTokens';

/** Дата "08.10.2026" из ISO; пусто или мусор: "-". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Дата и время "08.10.2026, 12:30" из ISO; пусто или мусор: "". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
